/**
 * Layer 1: MarketValidationChart (Pure Chart Core)
 * 
 * Invariants:
 * 1. Zero WebGL Allocation: chartOptions.nativeBackend = 'canvas2d' strictly enforced.
 * 2. Zero crypto endpoints, Core-7 symbols, or Trade Advisory types imported.
 * 3. Generation / Cancellation token defense against late async imports or race conditions.
 * 4. Error teardown and halt auto-retry on initialization exceptions.
 * 5. Full lifecycle cleanup: destroy chart instance, disconnect ResizeObserver, zero dangling RAF/listeners.
 * 6. Dynamic Geometry & Mark projection: Reference lines (SL/TP/BE) and entry band projected via Vela drawings;
 *    discrete TD9 setup marker projected via Vela marks.
 * 7. Instance Stability: Chart is instantiated once per container/theme; candle updates update data in-place
 *    without recreating canvas or destroying user pan/zoom state.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

export interface GenericOHLCVItem {
  time: number; // epoch milliseconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface GenericReferenceLine {
  id: string;
  price: number;
  rawPrice?: string;
  label: string;
  color: string;
  lineStyle?: 'solid' | 'dashed' | 'dotted';
}

export interface GenericBand {
  id: string;
  priceLow: number;
  priceHigh: number;
  rawPriceLow?: string;
  rawPriceHigh?: string;
  label: string;
  color: string;
}

export interface GenericTDMarker {
  time: number | null; // epoch ms
  count: number | null;
  direction: 'BUY' | 'SELL' | null;
  isAligned: boolean;
  message?: string;
}

export interface MarketValidationChartProps {
  candles: GenericOHLCVItem[];
  referenceLines?: GenericReferenceLine[];
  entryBand?: GenericBand | null;
  tdMarker?: GenericTDMarker | null;
  symbolLabel?: string;
  timeframeLabel?: string;
  height?: number | string;
  width?: number | string;
  className?: string;
  theme?: 'dark' | 'light';
  onResize?: () => void;
  formatPrice?: (price: number | string) => string;
}

export const MarketValidationChart: React.FC<MarketValidationChartProps> = ({
  candles,
  referenceLines = [],
  entryBand = null,
  tdMarker = null,
  symbolLabel = 'CHART',
  timeframeLabel = '1h',
  height = '100%',
  width = '100%',
  className = '',
  theme = 'dark',
  formatPrice: customFormatPrice,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<any>(null);
  const generationRef = useRef<number>(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const haltedOnErrorRef = useRef<boolean>(false);
  const latestCandlesRef = useRef<GenericOHLCVItem[]>(candles);
  latestCandlesRef.current = candles;
  const latestEntryBandRef = useRef<GenericBand | null | undefined>(entryBand);
  latestEntryBandRef.current = entryBand;
  const latestReferenceLinesRef = useRef<GenericReferenceLine[]>(referenceLines);
  latestReferenceLinesRef.current = referenceLines;
  const latestTdMarkerRef = useRef<GenericTDMarker | null | undefined>(tdMarker);
  latestTdMarkerRef.current = tdMarker;

  // Fallback formatter honoring raw string price or number
  const formatPriceFn = useCallback(
    (p: number | string) => {
      if (customFormatPrice) return customFormatPrice(p);
      if (typeof p === 'string') return p;
      if (!Number.isFinite(p)) return '--';
      if (p < 0.0001 && p > 0) return p.toFixed(8);
      if (p < 10 && p > 0) return p.toFixed(4);
      return p.toFixed(2);
    },
    [customFormatPrice]
  );

  // Compute mintick precision from minimum price in reference lines, entry bands, or candles
  const computedMinTick = useMemo(() => {
    let minPositive = Infinity;
    if (entryBand && entryBand.priceLow > 0) minPositive = Math.min(minPositive, entryBand.priceLow);
    for (const l of referenceLines) {
      if (l.price > 0) minPositive = Math.min(minPositive, l.price);
    }
    for (const c of candles) {
      if (c.close > 0) minPositive = Math.min(minPositive, c.close);
      if (c.low > 0) minPositive = Math.min(minPositive, c.low);
    }
    if (!Number.isFinite(minPositive) || minPositive <= 0) return 0.01;
    // Micro coins: 8 decimals
    if (minPositive < 0.0001) return 0.00000001;
    // Low micro: 6 decimals
    if (minPositive < 0.01) return 0.000001;
    // Small-coin 4-decimal presentation rule (< 10, e.g. SUI, ADA, DOGE)
    if (minPositive < 10) return 0.0001;
    // Standard / large coin: 2 decimals
    return 0.01;
  }, [entryBand, referenceLines, candles]);

  const latestComputedMinTickRef = useRef<number>(computedMinTick);
  latestComputedMinTickRef.current = computedMinTick;

  // Durable price precision configuration helper on chart feed and renderer
  const applyPrecision = useCallback((chart: any, minTick: number) => {
    if (!chart) return;
    try {
      const orchestrator = (chart as any).orchestrator;
      if (orchestrator?.feed) {
        orchestrator.feed.symbolInfo = () => ({ mintick: minTick });
      }
      const rawRenderer = orchestrator?.renderer || (chart.renderer as any)?.renderer;
      if (rawRenderer?.setPricePrecision) {
        rawRenderer.setPricePrecision(minTick);
      }
      if (rawRenderer?.scene) {
        rawRenderer.scene.priceMintick = minTick;
      }
    } catch {
      // ignore
    }
  }, []);

  // Synchronize geometry overlays (drawings & marks) onto chart instance
  const syncOverlays = useCallback(
    (chart: any) => {
      if (!chart || typeof chart.destroy !== 'function') return;

      // 1. Sync Reference Lines and Entry Band via chart.drawings
      const effectiveCandles = latestCandlesRef.current.length > 0 ? latestCandlesRef.current : candles;
      const effectiveEntryBand = latestEntryBandRef.current !== undefined ? latestEntryBandRef.current : entryBand;
      const effectiveRefLines = latestReferenceLinesRef.current || referenceLines;
      const effectiveTdMarker = latestTdMarkerRef.current !== undefined ? latestTdMarkerRef.current : tdMarker;
      const effectiveMinTick = latestComputedMinTickRef.current || computedMinTick;

      if (chart.drawings && chart.drawings.supported) {
        try {
          // Remove existing drawings
          const existingDrawings = chart.drawings.all?.() || [];
          if (existingDrawings.length > 0 && typeof chart.drawings.removeMany === 'function') {
            chart.drawings.removeMany(existingDrawings.map((d: any) => d.id));
          }

          // Add Entry Band (as Box drawing if anchors valid)
          if (effectiveEntryBand && effectiveCandles.length > 0) {
            const firstTime = effectiveCandles[0].time;
            const lastTime = effectiveCandles[effectiveCandles.length - 1].time;
            chart.drawings.add('box', {
              anchors: [
                { time: firstTime, price: effectiveEntryBand.priceHigh },
                { time: lastTime, price: effectiveEntryBand.priceLow },
              ],
              style: {
                color: 'rgba(56, 239, 125, 0.4)',
                fill: 'rgba(56, 239, 125, 0.15)',
                width: 1,
              },
            });
          }

          // Add Horizontal Reference Lines (Stop, Target, Breakeven)
          for (const line of effectiveRefLines) {
            if (Number.isFinite(line.price) && line.price > 0 && effectiveCandles.length > 0) {
              chart.drawings.add('hline', {
                anchors: [{ time: effectiveCandles[0].time, price: line.price }],
                style: {
                  color: line.color || '#38ef7d',
                  width: 1.5,
                  lineStyle: line.lineStyle || 'dashed',
                },
              });
            }
          }
        } catch {
          // Non-blocking geometry sync guard
        }
      }

      // 2. Sync DeMark TD9 Marker via chart.marks
      if (chart.marks && chart.marks.supported) {
        try {
          chart.marks.clear?.();
          if (
            effectiveTdMarker &&
            effectiveTdMarker.isAligned &&
            effectiveTdMarker.time !== null &&
            effectiveTdMarker.count !== null &&
            effectiveTdMarker.count >= 6
          ) {
            const isBuy = effectiveTdMarker.direction === 'BUY';
            chart.marks.add({
              id: 'td-marker-setup',
              time: effectiveTdMarker.time,
              glyph: {
                shape: 'circle',
                color: isBuy ? '#4ee39d' : '#ff7b7b',
                size: 8,
              },
              tooltip: `DeMark TD ${effectiveTdMarker.count} (${isBuy ? '买九' : '卖九'})`,
            });
          }
        } catch {
          // Non-blocking mark sync guard
        }
      }

      // 3. Set Price Precision on renderer if available (drives axis & tooltip decimals)
      applyPrecision(chart, effectiveMinTick);
    },
    [candles, entryBand, referenceLines, tdMarker, computedMinTick, applyPrecision]
  );

  const syncOverlaysRef = useRef(syncOverlays);
  syncOverlaysRef.current = syncOverlays;

  // Initialize chart instance once per container and theme
  useEffect(() => {
    const currentGen = ++generationRef.current;
    let isCancelled = false;

    // If halted on previous fatal constructor error, don't auto-retry without mount reset
    if (haltedOnErrorRef.current) {
      return;
    }

    async function initChart() {
      if (!containerRef.current) return;

      // Clean existing chart if theme changed or container changed
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.destroy?.();
        } catch {
          // ignore
        }
        chartInstanceRef.current = null;
      }

      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      try {
        const { Vela } = await import('@luxalgo/vela');

        if (isCancelled || currentGen !== generationRef.current) {
          return;
        }

        const targetCandles = latestCandlesRef.current.length > 0 ? latestCandlesRef.current : candles;
        const initialBars = targetCandles.map(c => ({
          time: c.time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
          volume: c.volume ?? 0,
        }));

        // HARD-LOCK: Strict Canvas2D native backend, zero WebGL
        const chartTheme = theme === 'dark' ? ('dark' as const) : ('light' as const);
        const chartOptions = {
          nativeBackend: 'canvas2d' as const, // Strict Canvas2D, prohibited from 'auto'
          theme: chartTheme,
          animations: false, // deterministic performance
          volume: true,
          priceStyle: 'candles',
          data: initialBars,
        };

        const chart = new Vela(containerRef.current, chartOptions as any);

        if (isCancelled || currentGen !== generationRef.current) {
          chart.destroy?.();
          return;
        }

        chartInstanceRef.current = chart;
        setInitError(null);
        haltedOnErrorRef.current = false;

        if (initialBars.length > 0) {
          lastCandlesSignatureRef.current = `${initialBars.length}_${initialBars[0].time}_${initialBars[0].close}_${initialBars[initialBars.length - 1].time}_${initialBars[initialBars.length - 1].close}`;
        } else {
          lastCandlesSignatureRef.current = 'empty';
        }

        // Apply durable precision
        applyPrecision(chart, latestComputedMinTickRef.current || computedMinTick);

        // Apply drawings and marks using latest ref
        syncOverlaysRef.current(chart);

        // Bind ResizeObserver
        if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
          if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
          }
          const ro = new ResizeObserver(() => {
            if (chartInstanceRef.current && typeof chartInstanceRef.current.resize === 'function') {
              chartInstanceRef.current.resize();
            }
          });
          ro.observe(containerRef.current);
          resizeObserverRef.current = ro;
        }
      } catch (err: any) {
        if (isCancelled || currentGen !== generationRef.current) return;
        haltedOnErrorRef.current = true; // Halt auto-retries
        setInitError(err?.message || '图表初始化异常');
        if (chartInstanceRef.current) {
          try {
            chartInstanceRef.current.destroy?.();
          } catch {
            // ignore
          }
          chartInstanceRef.current = null;
        }
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
      }
    }

    initChart();

    return () => {
      isCancelled = true;
      generationRef.current++;
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.destroy?.();
        } catch {
          // ignore
        }
        chartInstanceRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [theme, applyPrecision]); // Instantiated only on theme change or mount, NOT on candles update

  // Dedicated overlay geometry synchronization effect when entryBand, referenceLines, or tdMarker updates
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (chart && typeof chart.destroy === 'function') {
      syncOverlays(chart);
    }
  }, [entryBand, referenceLines, tdMarker, syncOverlays]);

  const lastCandlesSignatureRef = useRef<string>('');

  // In-place updates for candles and overlays without recreating Vela instance
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart || typeof chart.destroy !== 'function') return;

    try {
      const currentSignature = candles.length === 0
        ? 'empty'
        : `${candles.length}_${candles[0].time}_${candles[0].close}_${candles[candles.length - 1].time}_${candles[candles.length - 1].close}`;

      if (currentSignature === lastCandlesSignatureRef.current) {
        // Data unchanged: only sync overlays without resetting market or viewport
        syncOverlays(chart);
        return;
      }
      lastCandlesSignatureRef.current = currentSignature;

      const ohlcvBars = candles.map(c => ({
        time: c.time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: c.volume ?? 0,
      }));

      // Capture existing user visible range before market update to preserve pan/zoom
      const existingRange = typeof chart.getVisibleRange === 'function' ? chart.getVisibleRange() : null;

      const onUpdateDone = () => {
        // Preserve user's viewport after in-place data replacement
        if (existingRange && typeof chart.setVisibleRange === 'function') {
          try {
            chart.setVisibleRange(existingRange);
          } catch {
            // ignore range restore
          }
        }
        // Durable precision configuration
        applyPrecision(chart, latestComputedMinTickRef.current || computedMinTick);
        // Sync overlays onto stable chart instance
        syncOverlays(chart);
      };

      // In-place series update: prefer public setMarket, fall back to orchestrator.setBarSeries
      if (typeof chart.setMarket === 'function') {
        const updatePromise = chart.setMarket({ data: ohlcvBars });
        if (updatePromise && typeof updatePromise.then === 'function') {
          updatePromise.then(onUpdateDone).catch(() => {});
        } else {
          onUpdateDone();
        }
      } else if (chart.orchestrator?.setBarSeries) {
        chart.orchestrator.setBarSeries(ohlcvBars);
        onUpdateDone();
      } else {
        syncOverlays(chart);
      }
    } catch {
      // ignore in-place update errors
    }
  }, [candles, syncOverlays, computedMinTick, applyPrecision]);

  return (
    <div
      data-testid="market-validation-chart-root"
      className={`market-validation-chart-root ${className}`}
      style={{
        position: 'relative',
        width: width,
        height: height,
        minHeight: '400px',
        backgroundColor: 'var(--bg-canvas, #0f1411)',
        overflow: 'hidden',
        borderRadius: '6px',
        border: '1px solid var(--border-subtle, #1f2b23)',
      }}
    >
      {/* 1. Underlying Vela Canvas2D Container */}
      <div
        ref={containerRef}
        data-testid="vela-canvas-container"
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      />

      {/* 2. Error Fallback UI */}
      {initError && (
        <div
          data-testid="chart-error-banner"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(20, 10, 10, 0.85)',
            color: 'var(--color-down, #ff5e5e)',
            padding: '20px',
            textAlign: 'center',
            zIndex: 10,
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '6px' }}>
            ⚠️ 图表渲染中断 (Canvas2D Error)
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted, #8b9b90)' }}>
            {initError}
          </div>
        </div>
      )}

      {/* 3. Empty State Fallback */}
      {!initError && candles.length === 0 && (
        <div
          data-testid="chart-empty-state"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted, #8b9b90)',
            fontSize: '13px',
            fontFamily: 'var(--font-mono, monospace)',
          }}
        >
          暂无 K 线数据 (EMPTY)
        </div>
      )}

      {/* 4. Top Left HUD Overlay */}
      <div
        data-testid="chart-hud-overlay"
        style={{
          position: 'absolute',
          top: '10px',
          left: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            backgroundColor: 'var(--bg-surface-glass, rgba(17, 24, 20, 0.8))',
            border: '1px solid var(--border-subtle, #1f2b23)',
            borderRadius: '4px',
            padding: '3px 8px',
            fontSize: '12px',
            fontWeight: 700,
            fontFamily: 'var(--font-mono, monospace)',
            color: 'var(--text-primary, #e6eee8)',
          }}
        >
          {symbolLabel} · {timeframeLabel}
        </span>

        {/* TD9 Setup Badge */}
        {tdMarker && tdMarker.isAligned && tdMarker.count !== null && tdMarker.count >= 6 && (
          <span
            data-testid="td-setup-badge"
            style={{
              backgroundColor:
                tdMarker.direction === 'BUY'
                  ? 'rgba(46, 117, 89, 0.3)'
                  : 'rgba(201, 74, 74, 0.3)',
              border: `1px solid ${
                tdMarker.direction === 'BUY' ? 'var(--color-up, #2e7559)' : 'var(--color-down, #c94a4a)'
              }`,
              color:
                tdMarker.direction === 'BUY'
                  ? 'var(--color-up, #4ee39d)'
                  : 'var(--color-down, #ff7b7b)',
              borderRadius: '4px',
              padding: '3px 8px',
              fontSize: '11px',
              fontWeight: 700,
              fontFamily: 'var(--font-mono, monospace)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>{tdMarker.direction === 'BUY' ? '🟢' : '🔴'}</span>
            <span>DeMark TD {tdMarker.count}</span>
          </span>
        )}

        {candles.length > 0 && (
          <span
            style={{
              fontSize: '11px',
              color: 'var(--text-muted, #8b9b90)',
              fontFamily: 'var(--font-mono, monospace)',
            }}
          >
            {candles.length} bars (Canvas2D)
          </span>
        )}
      </div>

      {/* 5. Geometry Overlays Panel (Top Right HUD) */}
      <div
        data-testid="geometry-overlay-legend"
        style={{
          position: 'absolute',
          top: '10px',
          right: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '4px',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      >
        {entryBand && (
          <div
            data-testid="legend-entry-band"
            style={{
              backgroundColor: 'rgba(17, 24, 20, 0.85)',
              border: '1px solid var(--border-subtle, #1f2b23)',
              borderRadius: '3px',
              padding: '2px 6px',
              fontSize: '10px',
              color: 'var(--color-accent, #38ef7d)',
              fontFamily: 'var(--font-mono, monospace)',
            }}
          >
            入场区间 [ENTRY]: {formatPriceFn(entryBand.rawPriceLow ?? entryBand.priceLow)} ~ {formatPriceFn(entryBand.rawPriceHigh ?? entryBand.priceHigh)}
          </div>
        )}
        {referenceLines.map(line => (
          <div
            key={line.id}
            data-testid={`legend-line-${line.id}`}
            style={{
              backgroundColor: 'rgba(17, 24, 20, 0.85)',
              border: `1px dashed ${line.color}`,
              borderRadius: '3px',
              padding: '2px 6px',
              fontSize: '10px',
              color: line.color,
              fontFamily: 'var(--font-mono, monospace)',
            }}
          >
            {line.label}
          </div>
        ))}
      </div>
    </div>
  );
};
